/**
 * Normalizes a time value to "HH:MM", accepting either a plain "HH:MM"
 * (from the create/edit form's <input type="time">) or a full ISO
 * datetime string (as returned by the API, e.g. "2026-07-16T08:52:17.968Z").
 */
export function toHHMM(value) {
  if (!value) return "";
  // Full datetime, either "2026-07-16T09:00:00.000Z" (ISO) or
  // "2026-07-16 09:00" (backend's actual format) — just take the last
  // whitespace/T-separated "HH:mm" chunk, trimming any seconds/millis/zone.
  if (value.includes("T") || value.includes(" ")) {
    const timePart = value.split(/[T ]/)[1] || "";
    const [h, m] = timePart.split(":");
    if (h === undefined || m === undefined) return "";
    return `${h.padStart(2, "0")}:${m.padStart(2, "0")}`;
  }
  return value;
}

/** Parses "HH:MM" (or a full ISO datetime) into total minutes since midnight. Returns null if invalid/empty. */
function toMinutes(time) {
  const hhmm = toHHMM(time);
  if (!hhmm) return null;
  const [h, m] = hhmm.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

/**
 * A shift is overnight when Shift End Time is strictly earlier than Shift Start Time.
 * End Time equal to Start Time is a zero-length shift, not overnight.
 */
export function isOvernightShift(startTime, endTime) {
  const start = toMinutes(startTime);
  const end = toMinutes(endTime);
  if (start === null || end === null) return false;
  return end < start;
}

/** True only when start and end resolve to the exact same minute (zero-length shift). */
export function isZeroLengthShift(startTime, endTime) {
  const start = toMinutes(startTime);
  const end = toMinutes(endTime);
  if (start === null || end === null) return false;
  return start === end;
}

/**
 * Gross shift duration in hours.
 * Same-day: end - start. Overnight: (end + 24h) - start.
 * Returns null when start/end aren't both set yet.
 */
export function computeGrossDurationHours(startTime, endTime) {
  const start = toMinutes(startTime);
  let end = toMinutes(endTime);
  if (start === null || end === null) return null;
  if (end <= start) end += 24 * 60;
  return Math.round(((end - start) / 60) * 100) / 100;
}

/** Whole number, >= 0. Used for Late Arrival / Early Exit buffer minutes. */
export function isValidBufferMinutes(value) {
  if (value === "" || value === null || value === undefined) return false;
  const n = Number(value);
  return Number.isInteger(n) && n >= 0;
}

/** Numeric, >= 0, at most one decimal place. Used for working-hour and OT-hour fields. */
export function isValidOneDecimalHours(value) {
  if (value === "" || value === null || value === undefined) return false;
  if (!/^\d+(\.\d)?$/.test(String(value))) return false;
  return Number(value) >= 0;
}